import { Component, effect, inject, input, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { IonNav } from '@ionic/angular/common';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import {
	TenantSettingsDto,
	UpdateTenantSettingsDto,
} from '@splitsecondconnect/ultramarine-shared';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { InputFieldComponent } from '../../../shared/components/input-field/input-field.component';
import { ToggleNewComponent } from '../../../shared/components/toggle-new/toggle-new.component';
import { UltraButtonComponent } from '../../../shared/components/ultramarine-ui/ultra-button/ultra-button.component';
import { UltraPrimaryButtonComponent } from '../../../shared/components/ultramarine-ui/ultra-primary/ultra-primary.component';
import { PreferencesService } from '../../../shared/services/preferences.service';
import { ColorTheme } from '../../../shared/services/theme.service';
import { SettingsModel } from '../settings/models/settings.model';
import { SettingsService } from '../settings/services/settings.service';
import { SingleDropdownComponent } from '../../../shared/components/dropdowns/single-dropdown/single-dropdown.component';
import { SelectItem } from '../../../shared/models/select-item.type';
import {
	isoCodeMapper,
	languageIdMapper,
} from '../../../shared/mapper/language-id.mapper';

@Component({
	selector: 'app-preferences',
	templateUrl: 'preferences.component.html',
	styleUrls: ['preferences.component.scss'],
	imports: [
		TranslocoModule,
		InputFieldComponent,
		ToggleNewComponent,
		ReactiveFormsModule,
		FormsModule,
		UltraPrimaryButtonComponent,
		IonicModule,
		UltraButtonComponent,
		SingleDropdownComponent,
	],
})
export class PreferencesComponent {
	#preferences = inject(PreferencesService);
	#formBuilder = inject(FormBuilder);
	#settingsService = inject(SettingsService);
	#transloco = inject(TranslocoService);
	#langNames = new Intl.DisplayNames(['en'], { type: 'language' });

	langs: SelectItem<string>[] = this.#transloco
		.getAvailableLangs()
		.map((lang) => {
			const id = typeof lang === 'string' ? lang : lang.id;
			return {
				value: id,
				displayValue: this.#langNames.of(id) ?? id.toUpperCase(),
			};
		});

	#destroy$ = new Subject<void>();
	isDarkMode = signal(false);
	preferencesForm = this.#formBuilder.group({
		logoUri: [null],
		isTaxIncluded: [null],
		availabilityThreshold: [null],
		language: [],
	});
	#settings = signal<TenantSettingsDto>(null);
	isSubmitting = signal(false);
	navReference = input<IonNav>();

	constructor() {
		effect(() => {
			this.#preferences.setTheme(this.isDarkMode());
		});
	}

	ngOnInit() {
		this.#preferences.theme$
			.pipe(takeUntil(this.#destroy$))
			.subscribe((theme) => {
				this.isDarkMode.set(theme === ColorTheme.DARK);
			});

		const formModel = this.#mapDtoToForm(this.#settingsService.settings);
		this.#settings.set(this.#settingsService.settings);
		this.preferencesForm.setValue({
			logoUri: formModel.logoUri ?? null,
			isTaxIncluded: formModel.isTaxIncluded,
			availabilityThreshold: formModel.availabilityThreshold,
			language: formModel.language,
		});
	}

	#mapDtoToForm(dto: TenantSettingsDto): SettingsModel {
		return {
			logoUri: dto.logoUri,
			isTaxIncluded: dto.isTaxIncluded,
			availabilityThreshold: dto.availabilityThreshold,
			language: languageIdMapper(
				dto.defaultLanguageId,
				this.#settingsService.languages,
			),
		};
	}

	#mapFormToDto(): Partial<UpdateTenantSettingsDto> {
		return {
			id: this.#settings().id,
			logoUri: this.preferencesForm.controls.logoUri.value,
			isTaxIncluded: this.preferencesForm.controls.isTaxIncluded.value,
			availabilityThreshold:
				this.preferencesForm.controls.availabilityThreshold.value,
			defaultLanguageId: isoCodeMapper(
				this.preferencesForm.controls.language.value,
				this.#settingsService.languages,
			),
		};
	}

	onSubmit() {
		this.isSubmitting.set(true);
		this.#settingsService
			.updateSettings(this.#mapFormToDto())
			.pipe(
				finalize(() => {
					this.isSubmitting.set(false);
				}),
			)
			.subscribe();
	}

	onCancel() {
		const formModel = this.#mapDtoToForm(this.#settingsService.settings);
		this.#settings.set(this.#settingsService.settings);
		this.preferencesForm.setValue({
			logoUri: formModel.logoUri,
			isTaxIncluded: formModel.isTaxIncluded,
			availabilityThreshold: formModel.availabilityThreshold,
			language: formModel.language,
		});
		this.navReference().popToRoot();
	}

	ngOnDestroy() {
		this.#destroy$.next();
		this.#destroy$.complete();
	}
}
